import { AdvisorCodeInfo } from "@/common/context/advisorCodeInfoProvider";
import { ApplicationCreateResult, IQuoteCreateResult, IQuoteDto } from "@/common/data/quoteInterfaces";
import { IProductInfo } from "@/common/data/productInfoInterfaces";
import { UserInfo } from "@/common/data/ApplicationModels";
import { appConfig } from "@/common/config/appConfig";
import { getRequestHeaders } from "./helpers";

const parse = async (res: Response): Promise<any> => {
  if (!res) {
    throw "No Response Object";
  }

  const { ok, status } = res;
  let json = {};

  try {
    json = await res.json();
  } catch {}

  if (ok) {
    return json;
  }

  throw { status, ...json };

};

const buildUrl = (endpoint: string): string => {
  // concatenates base url and provided endpoint making sure there are no extra / in the result
  const result = `${appConfig.BFF_API_BASE_URL.replace(/\/$/, "")}/api/${endpoint.replace(/^\//, "")}`;

  return result;
};

const doFetch = async (endpoint: string, init: RequestInit | undefined): Promise<any> => {

  let response;
  try {

    response = await fetch(buildUrl(endpoint), init);

    if (response.ok) {
      const data = parse(response);

      return data;
    }

  } catch {
  }

  if ([404].includes(response?.status as number)) {
    redirectToErrorPage();
  } else if ([401, 403].includes(response?.status as number)) {
    redirectToDashboard();
  }

  console.error(response);

  throw undefined;
};

export const redirectToErrorPage = () => {
  const errorLocation = "/error";
  if (!window.location.pathname.startsWith(errorLocation)) {
    window.location.assign(errorLocation);
  }
};

export const redirectToDashboard = () => {
  const dashboardLocation = appConfig.EZCOMPLETE_DASHBOARD_URL;
  window.location.assign(dashboardLocation);
};

const get = async (endpoint: string): Promise<any> => {
  const res = doFetch(endpoint, {
    headers: getRequestHeaders(),
    credentials: "include",
  });

  return res;
};

const post = async (endpoint: string, data?: object): Promise<any> => {
  const res = doFetch(endpoint, {
    method: "POST",
    headers: getRequestHeaders(),
    credentials: "include",
    body: data != null ? JSON.stringify(data) : "",
  });

  return res;
};

const put = async (endpoint: string, data: object): Promise<any> => {
  const res = doFetch(endpoint, {
    method: "PUT",
    headers: getRequestHeaders(),
    credentials: "include",
    body: JSON.stringify(data),
  });

  return res;
};

export const getProducts = (productCode: string): Promise<IProductInfo> => {
  return get(`products/${productCode}`).catch(() => redirectToErrorPage());
};

export const createSaveDraftQuotes = (data: IQuoteDto): Promise<IQuoteCreateResult> => {
  return post("quotes", data);
};

export const updateSaveDraftQuotes = (data: IQuoteDto): Promise<void> => {
  return put("quotes", data);
};

export const loadDraftData = (illustrationNumber: string): Promise<IQuoteDto> => {
  return get(`quotes/${illustrationNumber}`);
};

export const saveDraftData = (requestBody: any): Promise<IQuoteCreateResult | void> => {
  if (!!requestBody.number) {
    return updateSaveDraftQuotes(requestBody);
  }

  return createSaveDraftQuotes(requestBody);
};

export const getAdvisorCodes = (): Promise<AdvisorCodeInfo[]> => {
  return get("AdvisorCodes/GetAdvisorCodes").catch(() => redirectToErrorPage());
};

// fetch premium
export const fetchPremiumInfo = (data: any): Promise<any> => {
  return post("illustrationgateway", data);
};

export const submitFormData = (data: IQuoteDto): Promise<ApplicationCreateResult> => {
  return post("quotes/convertToApplication", data).then((result: ApplicationCreateResult) => {
    if ((result.validationResults?.length || 0) === 0) {
      return result;
    }

    throw new Error(JSON.stringify(result));
  });
};

export const getUserInfo = (): Promise<UserInfo> => {
  return get("AdvisorCodes/UserInfo");
};

